import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Checkbox, Modal, Space, Spin, Typography, message } from 'antd'
import {
  BankOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RollbackOutlined,
  WalletOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { getMerchant } from '../../services/merchant'
import {
  acceptMerchantAgreement,
  getCurrentMerchantAgreement,
  getMerchantAgreementStatus,
  type MerchantAgreementDocument,
  type MerchantAgreementStatus,
} from '../../services/merchantAgreement'
import { ApiError } from '../../services/api'
import { useMerchantStore } from '../../stores/merchantStore'
import MerchantAgreementDocumentView from './MerchantAgreementDocumentView'

const { Text, Title } = Typography
const STALE_RENDER_CODES = new Set([40911, 40912, 40913])
const MAX_TIMER_DELAY_MS = 2_147_483_647
const DEADLINE_REFRESH_BUFFER_MS = 25
const PAST_DEADLINE_RETRY_MS = 1_000
const SAFE_OPERATION_ROUTES = [
  '/dashboard/orders',
  '/dashboard/disputes',
  '/dashboard/balance',
  '/dashboard/payouts',
  '/dashboard/bank-accounts',
]

interface MerchantAgreementGateProps {
  children: ReactNode
  urgentAnnouncementActive: boolean
  onAgreementDialogsClosed: () => void
}

function isSafeOperationRoute(pathname: string) {
  return SAFE_OPERATION_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))
}

function formatHongKongDeadline(timestamp: number) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(timestamp)
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}（香港時間）`
}

export default function MerchantAgreementGate({
  children,
  urgentAnnouncementActive,
  onAgreementDialogsClosed,
}: MerchantAgreementGateProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const setMerchant = useMerchantStore((state) => state.setMerchant)
  const [status, setStatus] = useState<MerchantAgreementStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusFailed, setStatusFailed] = useState(false)
  const [document, setDocument] = useState<MerchantAgreementDocument | null>(null)
  const [signingOpen, setSigningOpen] = useState(false)
  const [documentLoading, setDocumentLoading] = useState(false)
  const [documentFailed, setDocumentFailed] = useState(false)
  const [consented, setConsented] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const inFlightStatusRefresh = useRef<Promise<MerchantAgreementStatus> | null>(null)
  const statusRequestSequence = useRef(0)
  const blockingDialogOpen = useRef(false)
  const signingDialogVisible = useRef(false)
  const urgentAnnouncementActiveRef = useRef(urgentAnnouncementActive)
  urgentAnnouncementActiveRef.current = urgentAnnouncementActive

  const refreshStatus = useCallback((force = false): Promise<MerchantAgreementStatus> => {
    if (!force && inFlightStatusRefresh.current) return inFlightStatusRefresh.current

    const requestSequence = ++statusRequestSequence.current
    setStatusLoading(true)
    const request = getMerchantAgreementStatus()
      .then((response) => {
        if (requestSequence === statusRequestSequence.current) {
          setStatus(response.data)
          setStatusFailed(false)
        }
        return response.data
      })
      .catch((error: unknown) => {
        if (requestSequence === statusRequestSequence.current) setStatusFailed(true)
        throw error
      })
      .finally(() => {
        if (requestSequence === statusRequestSequence.current) {
          inFlightStatusRefresh.current = null
          setStatusLoading(false)
        }
      })
    inFlightStatusRefresh.current = request
    return request
  }, [])

  useEffect(() => {
    refreshStatus().catch(() => {})
  }, [refreshStatus])

  useEffect(() => {
    const refreshWhenActive = () => {
      void refreshStatus().catch(() => {})
    }
    const refreshWhenVisible = () => {
      if (window.document.visibilityState === 'visible') refreshWhenActive()
    }

    window.addEventListener('focus', refreshWhenActive)
    window.document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshWhenActive)
      window.document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [refreshStatus])

  useEffect(() => {
    if (
      !status?.requiresAction ||
      status.accepted ||
      status.capabilityBlocked ||
      status.graceEndsAt == null
    ) {
      return
    }

    const remaining = status.graceEndsAt - Date.now()
    const delay = remaining > 0
      ? Math.min(remaining + DEADLINE_REFRESH_BUFFER_MS, MAX_TIMER_DELAY_MS)
      : PAST_DEADLINE_RETRY_MS
    let cancelled = false
    let timeout: number
    const refreshAtDeadline = async () => {
      try {
        await refreshStatus()
      } catch {
        if (!cancelled) timeout = window.setTimeout(refreshAtDeadline, PAST_DEADLINE_RETRY_MS)
      }
    }
    timeout = window.setTimeout(refreshAtDeadline, delay)
    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [refreshStatus, status])

  const loadDocument = useCallback(async () => {
    setDocumentLoading(true)
    setDocumentFailed(false)
    try {
      const response = await getCurrentMerchantAgreement()
      setDocument(response.data)
    } catch {
      setDocumentFailed(true)
    } finally {
      setDocumentLoading(false)
    }
  }, [])

  const openSigning = () => {
    setConsented(false)
    setSigningOpen(true)
    if (!document) void loadDocument()
  }

  const closeSigning = () => {
    if (!accepting) {
      setSigningOpen(false)
      setConsented(false)
    }
  }

  const acceptAgreement = async () => {
    if (!document || !consented) return
    setAccepting(true)
    try {
      await acceptMerchantAgreement({
        accepted: true,
        agreementVersion: document.agreementVersion,
        effectiveDate: document.effectiveDate,
        renderedDocumentSha256: document.renderedDocumentSha256,
      })

      const [statusResult, merchantResult] = await Promise.allSettled([
        refreshStatus(true),
        getMerchant(),
      ])
      if (statusResult.status === 'rejected') throw statusResult.reason
      if (merchantResult.status === 'fulfilled') setMerchant(merchantResult.value.data)
      setSigningOpen(false)
      setConsented(false)
      message.success('協議補簽完成')
    } catch (error: unknown) {
      if (error instanceof ApiError && error.code && STALE_RENDER_CODES.has(error.code)) {
        setConsented(false)
        await Promise.allSettled([loadDocument(), refreshStatus()])
        message.warning('協議內容已更新，請重新閱讀並確認')
      } else {
        message.error(error instanceof Error ? error.message : '協議接受失敗，請重試')
      }
    } finally {
      setAccepting(false)
    }
  }

  const requiresAction = Boolean(status?.requiresAction && !status.accepted)
  const restrictedPageBlocked = Boolean(
    requiresAction && status?.capabilityBlocked && !isSafeOperationRoute(location.pathname),
  )
  const blockingGateOpen = restrictedPageBlocked && !signingOpen && !urgentAnnouncementActive
  const signingDialogOpen = signingOpen && !urgentAnnouncementActive

  useEffect(() => {
    if (blockingGateOpen) blockingDialogOpen.current = true
  }, [blockingGateOpen])

  useEffect(() => {
    if (signingDialogOpen) signingDialogVisible.current = true
  }, [signingDialogOpen])

  useEffect(() => {
    if (urgentAnnouncementActive && !blockingDialogOpen.current && !signingDialogVisible.current) {
      onAgreementDialogsClosed()
    }
  }, [onAgreementDialogsClosed, urgentAnnouncementActive])

  const handleAgreementDialogAfterClose = useCallback((dialog: 'blocking' | 'signing') => {
    if (dialog === 'blocking') blockingDialogOpen.current = false
    else signingDialogVisible.current = false

    if (
      urgentAnnouncementActiveRef.current
      && !blockingDialogOpen.current
      && !signingDialogVisible.current
    ) {
      onAgreementDialogsClosed()
    }
  }, [onAgreementDialogsClosed])

  const banner = useMemo(() => {
    if (!requiresAction || !status) return null

    if (status.approvalBlocked) {
      return {
        type: 'warning' as const,
        message: 'Merchant Services Agreement 尚待補簽',
        description: '補簽後才可繼續審批。既有認證材料和申請狀態不會變更。',
      }
    }
    if (status.capabilityBlocked) {
      return {
        type: 'error' as const,
        message: 'Merchant Services Agreement 補簽已逾期',
        description: '新增交易能力已暫停；退款、争议、餘額結算和提現仍可使用。',
      }
    }
    if (status.graceEndsAt) {
      return {
        type: 'warning' as const,
        message: '請在寬限期內完成 Merchant Services Agreement 補簽',
        description: `截止時間：${formatHongKongDeadline(status.graceEndsAt)}。寬限期內現有能力保持可用。`,
      }
    }
    return {
      type: 'warning' as const,
      message: 'Merchant Services Agreement 尚待補簽',
      description: '請完成補簽，既有認證材料和申請狀態不會變更。',
    }
  }, [requiresAction, status])

  const goTo = (pathname: string) => navigate(pathname)

  return (
    <>
      {banner && (
        <Alert
          type={banner.type}
          showIcon
          message={banner.message}
          description={banner.description}
          action={(
            <Button type="primary" icon={<FileTextOutlined />} onClick={openSigning}>
              立即簽署
            </Button>
          )}
          style={{ marginBottom: 20 }}
        />
      )}

      {statusFailed && (
        <Alert
          type="error"
          showIcon
          message="無法確認 Merchant Services Agreement 狀態"
          description="請重新檢查協議狀態；狀態恢復前請勿建立新的交易能力。"
          action={(
            <Button
              icon={<ReloadOutlined />}
              loading={statusLoading}
              onClick={() => void refreshStatus().catch(() => {})}
            >
              重新檢查協議狀態
            </Button>
          )}
          style={{ marginBottom: 20 }}
        />
      )}

      {children}

      <Modal
        open={blockingGateOpen}
        afterClose={() => handleAgreementDialogAfterClose('blocking')}
        title="Merchant Services Agreement 補簽要求"
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width={640}
        style={{ maxWidth: 'calc(100vw - 24px)' }}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="error"
            showIcon
            message="新增交易能力已暫停"
            description="完成協議補簽後將立即恢復。退款、争议處理、餘額結算和提現不受影響。"
          />
          <Button type="primary" block icon={<FileTextOutlined />} onClick={openSigning}>
            立即簽署
          </Button>
          <Space wrap size={[8, 8]}>
            <Button icon={<RollbackOutlined />} onClick={() => goTo('/dashboard/orders')}>訂單退款</Button>
            <Button icon={<RollbackOutlined />} onClick={() => goTo('/dashboard/balance/refunds')}>退款記錄</Button>
            <Button icon={<WarningOutlined />} onClick={() => goTo('/dashboard/disputes')}>争议案件</Button>
            <Button icon={<WalletOutlined />} onClick={() => goTo('/dashboard/balance')}>餘額與結算</Button>
            <Button icon={<BankOutlined />} onClick={() => goTo('/dashboard/payouts')}>結款與提現</Button>
          </Space>
        </Space>
      </Modal>

      <Modal
        open={signingDialogOpen}
        afterClose={() => handleAgreementDialogAfterClose('signing')}
        title="補簽 Merchant Services Agreement"
        onCancel={closeSigning}
        closable={!accepting}
        maskClosable={!accepting}
        keyboard={!accepting}
        width={900}
        style={{ top: 20, maxWidth: 'calc(100vw - 24px)' }}
        footer={(
          <Space wrap>
            <Button onClick={closeSigning} disabled={accepting}>稍後處理</Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={!document || !consented || documentFailed}
              loading={accepting}
              onClick={acceptAgreement}
            >
              確認並接受協議
            </Button>
          </Space>
        )}
      >
        {documentLoading ? (
          <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : documentFailed || !document ? (
          <Alert
            type="error"
            showIcon
            message="協議暫時無法載入"
            description={<Button type="link" onClick={() => void loadDocument()}>重新載入</Button>}
          />
        ) : (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="合同相對方"
              description={status?.merchantLegalName || '暫時無法確認合同相對方'}
            />
            <div>
              <Title level={5} style={{ margin: 0 }}>KyrenPay Merchant Services Agreement</Title>
              <Text type="secondary">版本 {document.agreementVersion} · Effective Date：{document.effectiveDateDisplay}</Text>
            </div>
            <div
              data-testid="legacy-merchant-agreement-body"
              className="merchant-agreement-scroll"
              style={{ maxHeight: 'min(48vh, 460px)' }}
            >
              <MerchantAgreementDocumentView markdownContent={document.markdownContent} />
            </div>
            <Checkbox checked={consented} onChange={(event) => setConsented(event.target.checked)}>
              {document.acceptanceStatement}
            </Checkbox>
          </Space>
        )}
      </Modal>
    </>
  )
}
